import { diffDays } from "@insightfulscience/shared-utils/dates/dateDiff";
import { parseFullName } from "@insightfulscience/shared-utils/parsers/parseFullName";
import { isGroupPlan, isIsul, isIsulGroup, isPersonalPlan } from "./plan";
import { daysBeforeNow } from "../utils/daysBeforeNow";
import { publicConfig } from "../config";

const DAYS_TO_ALLOW_RENEW = 60;

export const SUBSCRIPTION_STATUS = {
	ACTIVE: "active",
	CANCELLED: "cancelled",
	EXPIRED: "expired",
	DUNNING: "past-due",
};

export const ACTION_STATUSES = {
	REACTIVATED_SUCCESSFULLY: {
		status: "success",
	},
	CANCELLED_SUCCESSFULLY: {
		status: "success",
	},
	BILLING_INFO_UPDATED_SUCCESSFULLY: {
		status: "success",
	},
	UPDATED_PAYMENT_METHOD: "UPDATED_PAYMENT_METHOD",
	SUBSCRIPTION_WAS_UPDATED: "SUBSCRIPTION_WAS_UPDATED",
	SUBSCRIPTION_PLAN_WAS_CHANGED: "SUBSCRIPTION_PLAN_WAS_CHANGED",
};

const ORDER_STATUS = {
	PENDING: "pending",
	CANCELLED: "cancelled",
};

export const getAlertInfo = action => action && ACTION_STATUSES[action.toUpperCase()];

export const hasSusbscriptionDunning = subscription =>
	subscription.status === SUBSCRIPTION_STATUS.ACTIVE &&
	Array.isArray(subscription.orders) &&
	subscription.orders.some(
		({ status, createdBy }) => status === ORDER_STATUS.PENDING && createdBy === "webhook",
	);

/** @typedef {"active" | "canceled" | "expired"} Status */

/** @type {(subsciption: {isAutoBillingEnabled: Boolean, expirationDate: string | Date, status: Status }) => boolean} */
export const isSubscriptionExpired = ({ isAutoBillingEnabled, expirationDate, status }) => {
	const now = new Date();
	return isAutoBillingEnabled
		? status === SUBSCRIPTION_STATUS.EXPIRED
		: diffDays(now, new Date(expirationDate)) < 0 || status === SUBSCRIPTION_STATUS.EXPIRED;
};

/** @type {(subsciption: { status: Status }) => boolean} */
export const isSubscriptionCancelled = subscription =>
	subscription.status === SUBSCRIPTION_STATUS.CANCELLED;

/** @type {(subsciption: { status: Status }) => boolean} */
export const isSubscriptionActive = subscription =>
	subscription.status === SUBSCRIPTION_STATUS.ACTIVE &&
	!isSubscriptionExpired(subscription) &&
	!hasSusbscriptionDunning(subscription);

/** @type {(subsciption: { status: Status, archiveBit: boolean }) => boolean} */
export const isSubscriptionReturned = subscription =>
	subscription.status === SUBSCRIPTION_STATUS.EXPIRED && subscription.archiveBit;

/** @type {(subsciption: { isAutoBillingEnabled: boolean }) => boolean} */
export const isAutoBillingSubscription = subscription => subscription.isAutoBillingEnabled;

/** @type {(plan: { type, planCode }) => boolean} */
export const isPersonalUpgradeToGroupAllowed = plan =>
	isIsul(plan.planCode) && isPersonalPlan(plan) ? publicConfig.IS_ISUL_GROUP_ENABLED : true;

/** @type {(plan: { type, planCode }) => boolean} */
export const isRenewToGroupAllowed = plan =>
	isIsul(plan.planCode) && isPersonalPlan(plan) ? publicConfig.IS_ISUL_GROUP_ENABLED : true;

export const isRenewGroupAllowed = plan =>
	isIsul(plan.planCode) && isGroupPlan(plan) ? publicConfig.IS_ISUL_GROUP_ENABLED : true;

/** @type {(plan: { type, planCode }) => boolean} */
export const isPurchaseActivationAllowed = plan =>
	isIsul(plan.planCode) && isGroupPlan(plan) ? publicConfig.IS_ISUL_GROUP_ENABLED : true;

/** @type {(subscription: { plan: { type, planCode }, isTrial }) => boolean} */
export const isTrialUpgradeToGroupAllowed = ({ plan, isTrial }) =>
	isIsul(plan.planCode) && isTrial ? publicConfig.IS_ISUL_GROUP_ENABLED : true;

/** @type {(planCode) => boolean} */
export const isPurchaseGroupAllowed = planCode =>
	isIsulGroup(planCode) ? publicConfig.IS_ISUL_GROUP_ENABLED : true;

/** @type {(subsciption: { allowedOperations: AllowedOperations }) => boolean} */
export const isAllowSubscriptionAddonOperation = subscription =>
	subscription?.allowedOperations?.addons && isPurchaseActivationAllowed(subscription.plan);

/** @type {(subsciption: { allowedOperations: AllowedOperations, plan: { planCode } }) => boolean} */
export const isAllowSubscriptionChangePlanOperation = subscription =>
	subscription?.allowedOperations?.changePlan && isPersonalUpgradeToGroupAllowed(subscription.plan);

/** @type {(subsciption: { allowedOperations: AllowedOperations }) => boolean} */
export const isAllowSubscriptionViewInvoiceOperation = subscription =>
	subscription?.allowedOperations?.viewInvoice;

/** @type {(subsciption: { allowedOperations: AllowedOperations }) => boolean} */
export const isAllowSubscriptionRenewalOperation = subscription =>
	subscription?.allowedOperations?.renewal && isRenewGroupAllowed(subscription.plan);

/** @type {(subsciption: {
	status: Status;
	isAutobilling: boolean;
	expirationDate: string | Date;
}) => boolean} */
export const allowSubscriptionRenew = subsciption =>
	// prettier-ignore
	!isAutoBillingSubscription(subsciption) && (
		(isSubscriptionActive(subsciption) &&
			daysBeforeNow(subsciption.expirationDate) <= DAYS_TO_ALLOW_RENEW) ||
		isSubscriptionExpired(subsciption)
	);

/** @type {(subsciption: {
	status: Status;
	isAutobilling: boolean;
	expirationDate: string | Date;
}) => "EXPIRED" | "RENEWS" | "EXPIRES"} */
export const statusEventName = subscription => {
	const isExpired = isSubscriptionExpired(subscription);
	const isActive = isSubscriptionActive(subscription);
	const isAutoBillingEnabled = isAutoBillingSubscription(subscription);
	const isDunning = hasSusbscriptionDunning(subscription);

	return (
		((isExpired && "EXPIRED") ||
		(isAutoBillingEnabled && (isActive || isDunning) && "RENEWS") ||
		"EXPIRES")
	);
};

/** @type {(subsciption: {
	status: Status;
	plan: {};
	isAutobilling: boolean;
	expirationDate: string | Date;
}) => boolean} */
export const allowSubscriptionAddons = subscription =>
	subscription.plan != null &&
	isSubscriptionActive(subscription) &&
	isGroupPlan(subscription.plan) &&
	!hasSusbscriptionDunning(subscription) &&
	isAllowSubscriptionAddonOperation(subscription);

/** @type {(subscription: {
 	billingName: string | undefined;
	billingEmail: string | undefined;
 	billingCompany: string | undefined;
	billingDepartment: string | undefined;
	billingAddress1: string | undefined;
	billingAddress2: string | undefined;
	billingCity: string | undefined;
	billingState: string | undefined;
	billingZipCode: string | undefined;
	billingCountryID: number | undefined;
	billingPhoneNumber: string | undefined;
 }) => {
	company: string | undefined;
	address1: string | undefined;
	address2: string | undefined;
	address3: string | undefined;
	city: string | undefined;
	state: string | undefined;
	zipCode: string | undefined;
	countryID: number | undefined;
	phoneNumber: string | undefined;
 }} */
export const getBillingInfo = ({
	billingName,
	billingEmail,
	billingCompany,
	billingDepartment,
	billingAddress1,
	billingAddress2,
	billingCity,
	billingState,
	billingZipCode,
	billingCountryID,
	billingPhoneNumber,
}) => ({
	email: billingEmail,
	...parseFullName(billingName),
	company: billingCompany,
	address1: billingDepartment,
	address2: billingAddress1,
	address3: billingAddress2,
	city: billingCity,
	state: billingState,
	zipCode: billingZipCode,
	countryID: billingCountryID,
	phone: billingPhoneNumber,
});

export const isManualSubscription = ({ externalID, isAutoBillingEnabled }) =>
	externalID === null && !isAutoBillingEnabled;

export const isActivationManagementAllowed = ({ serialNumbers }) =>
	!!serialNumbers.find(item => item.isActivationManagementAllowed === true);

export const subscriptionActiveActivations = serialNumbers =>
	serialNumbers[0].activations.filter(item => item.activationStatus === "active");
export const subscriptionDeactivatedActivations = serialNumbers =>
	serialNumbers[0].activations.filter(item => item.activationStatus === "deactivated");
export const subscriptionBlacklistedActivations = serialNumbers =>
	serialNumbers[0].activations.filter(item => item.activationStatus === "blacklisted");

export const allowChangePlan = subscription =>
	isPersonalPlan(subscription.plan) &&
	subscription.isActive &&
	!subscription.hasDunning &&
	isAutoBillingSubscription(subscription) &&
	isAllowSubscriptionChangePlanOperation(subscription);

export const MIN_TRANS_AMOUNT = 0.5;

export const isPurchaseSubscription = subscription =>
	subscription.allowAddons && subscription.isActive;
export const isRenewSubscription = (subscription, forceRenew) =>
	!subscription.isTrial &&
	((forceRenew && !subscription.isAutoBillingEnabled) || subscription.allowRenew);

export const isSerialNumberGenerationPending = subscription =>
	subscription?.lastOrder?.isSerialNumberGenerationPending;

export const isHasPaymentMethod = subscription => subscription.paymentInfo !== null;
